/**
 * Gates local-library Tauri IPC. v11.0.0: every Connector .app is a home master —
 * UI visibility uses isTauriRuntimeAvailable() only. IPC probes still run on boot
 * for migration-banner detection and local DB queryability checks.
 */

import {
  isIpcPostMessageFallbackActive,
  listen as connectorListen,
  connectorInvoke,
  isConnectorAvailable,
} from '../lib/connector';

let _localLibraryIpcAvailable: boolean | null = null;
/** True when this machine has a scanned local SQLite catalog (album count > 0). */
let _localLibraryHasAlbums: boolean | null = null;
/** Set after the first host probe completes (success or failure). */
let _hostProbeComplete = false;
/** True after connector catalog overlay init — get_albums may return real rows. */
let _localCatalogBrowseReady = false;
/** True when Mycelium reports a reachable home-library tunnel (another machine owns the catalog). */
let _remoteHomeLibraryAvailable = false;
/** Persisted remote catalog in cache — used by non-Tauri remote viewers only. */
let _cachedRemoteLibraryHint = false;
let _coreLocalLibraryIpcConfirmed = false;
let _probePromise: Promise<boolean> | null = null;
let _hostProbePromise: Promise<boolean> | null = null;

/** Home library file-server probe scheduling (backoff + source-offline gate). */
export type FileServerProbeResult = {
  ok: boolean;
  reason?: string;
  status?: number;
  error?: string;
  skipped?: boolean;
  skipReason?: 'sources-offline' | 'backoff' | 'no-tauri';
};

let _fileServerProbeFailures = 0;
let _fileServerProbeInFlight = false;
let _fileServerProbePromise: Promise<FileServerProbeResult> | null = null;
let _fileServerProbeLastAttemptMs = 0;
let _fileServerProbeLastResult: FileServerProbeResult = { ok: false };
let _sourceHealthSnapshot: Record<string, { status?: string }> = {};
let _fileServerProbeGateInitialized = false;

function backoffMsAfterFailures(consecutiveFailures: number): number {
  if (consecutiveFailures <= 1) return 5_000;
  if (consecutiveFailures === 2) return 15_000;
  return 60_000;
}

function effectiveProbeBackoffMs(consecutiveFailures: number): number {
  const base = consecutiveFailures === 0 ? 0 : backoffMsAfterFailures(consecutiveFailures);
  const scaled = base === 0 ? 0 : Math.max(5_000, base);
  return isIpcPostMessageFallbackActive() ? scaled * 2 : scaled;
}

/** Read-only snapshot of Connector source health (source_id → status). */
export function getSourceHealthSnapshot(): Readonly<
  Record<string, { status?: string; reason?: string; since_seconds?: number }>
> {
  return _sourceHealthSnapshot;
}

/** True when every configured source is offline (skip pointless file-server probes). */
export function areAllLibrarySourcesOffline(): boolean {
  const entries = Object.values(_sourceHealthSnapshot);
  if (entries.length === 0) return false;
  return entries.every((s) => s.status === 'offline');
}

function resetFileServerProbeBackoff(): void {
  _fileServerProbeFailures = 0;
}

/** Subscribe to source-health-changed and seed initial health (call once on Connector boot). */
export function initHomeLibraryFileServerProbeGate(): void {
  if (_fileServerProbeGateInitialized || !isTauriRuntimeAvailable()) return;
  _fileServerProbeGateInitialized = true;

  if (isConnectorAvailable()) {
    connectorInvoke('check_source_health')
      .then((data) => {
        if (data && typeof data === 'object') {
          _sourceHealthSnapshot = data as Record<string, { status?: string }>;
        }
      })
      .catch((err) => {
        console.warn('[localLibraryIpcGate] check_source_health failed:', err);
      });
  }

  connectorListen('source-health-changed', (payload) => {
    const { source_id, status, reason, since_seconds } = (payload || {}) as {
      source_id?: string;
      status?: string;
      reason?: string;
      since_seconds?: number;
    };
    if (!source_id) return;
    const wasAllOffline = areAllLibrarySourcesOffline();
    _sourceHealthSnapshot[source_id] = status === 'online'
      ? { status: 'online' }
      : { status: 'offline', reason, since_seconds: since_seconds as number | undefined };
    if (status === 'online' && wasAllOffline) {
      resetFileServerProbeBackoff();
      void probeHomeLibraryFileServerWithBackoff({ force: true });
    }
  }).catch(() => {});
}

export function isTauriRuntimeAvailable(): boolean {
  const w = window as Window & { __TAURI__?: unknown; __TAURI_INTERNALS__?: unknown };
  return typeof window !== 'undefined' && !!(w.__TAURI__ || w.__TAURI_INTERNALS__);
}

export function isLocalLibraryCommandError(err: unknown): boolean {
  const msg = String((err as { message?: string })?.message ?? err ?? '');
  return /not allowed by ACL|unknown command|command .+ not found/i.test(msg);
}

/** Wait for host probe before routing library calls (avoids racing the boot probe). */
export async function ensureLibraryRoutingReady(): Promise<void> {
  if (!isTauriRuntimeAvailable()) return;
  initLocalCatalogBrowseGate();
  if (_hostProbeComplete && _localCatalogBrowseReady) return;
  await probeLocalLibraryHost();
  if (!_localCatalogBrowseReady && isTauriRuntimeAvailable()) {
    await waitForLocalCatalogBrowseReady(45_000);
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Poll until catalog browse gate opens or timeout (init window). */
export async function waitForLocalCatalogBrowseReady(timeoutMs = 45_000): Promise<boolean> {
  if (!isTauriRuntimeAvailable()) return true;
  if (_localCatalogBrowseReady) return true;
  initLocalCatalogBrowseGate();
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    if (_localCatalogBrowseReady) return true;
    await sleep(250);
  }
  if (!_localCatalogBrowseReady && _hostProbeComplete) {
    markLocalCatalogBrowseReady();
  }
  return _localCatalogBrowseReady;
}

export function markLocalLibraryIpcUnavailable(reason?: string): void {
  if (_localLibraryIpcAvailable !== false) {
    console.warn(
      '[libraryService] Local library IPC unavailable — using cloud API',
      reason || '',
    );
  }
  _localLibraryIpcAvailable = false;
}

/** v11.0.0: home Connector = any Tauri runtime. Explicit remote role deferred to v11.1. */
export function isHomeLibraryHost(): boolean {
  return isTauriRuntimeAvailable();
}

/** True when local_library_get_stats succeeded at least once this session. */
export function isLocalLibraryIpcQueryable(): boolean {
  return _localLibraryIpcAvailable === true;
}

/** True after probeLocalLibraryHost() has finished at least once. */
export function isLibraryHostProbeComplete(): boolean {
  return _hostProbeComplete;
}

/** True when connector catalog overlay init finished — safe to treat get_albums total=0 as empty. */
export function isLocalCatalogBrowseReady(): boolean {
  if (!isTauriRuntimeAvailable()) return true;
  return _localCatalogBrowseReady;
}

export function markLocalCatalogBrowseReady(albumCount?: number): void {
  if (_localCatalogBrowseReady) return;
  _localCatalogBrowseReady = true;
  if (typeof albumCount === 'number' && Number.isFinite(albumCount)) {
    _localLibraryHasAlbums = albumCount > 0;
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('maestro:local-catalog-ready', {
      detail: { albumCount: albumCount ?? null },
    }));
  }
}

/** Subscribe to connector catalog-ready + seed from IPC probe (call once on Connector boot). */
export function initLocalCatalogBrowseGate(): void {
  if (!isTauriRuntimeAvailable()) return;
  connectorListen('local-core:catalog-ready', () => {
    markLocalCatalogBrowseReady();
  }).catch(() => {});
  connectorListen('local-core:catalog-changed', (payload) => {
    const catalogReason = (payload as { reason?: string } | undefined)?.reason;
    try {
      window.dispatchEvent(new CustomEvent('maestro:library:changed', {
        detail: {
          reason: 'connector_catalog_changed',
          catalogReason: catalogReason ?? undefined,
          forceFullRefresh: true,
        },
      }));
    } catch { /* ignore */ }
  }).catch(() => {});
}

/** Non-Tauri remote viewers only — always false on Connector .app (v11.0.0). */
export function isRemoteLibraryViewer(): boolean {
  if (isTauriRuntimeAvailable()) return false;
  return isRemoteConnectorShell()
    && (_remoteHomeLibraryAvailable || _cachedRemoteLibraryHint);
}

/** Non-Tauri remote Connector shells only — always false on Connector .app (v11.0.0). */
export function isRemoteConnectorShell(): boolean {
  if (isTauriRuntimeAvailable()) return false;
  if (isHomeLibraryHost()) return false;
  if (_hostProbeComplete) return true;
  if (_localLibraryIpcAvailable === false) return true;
  return false;
}

export function markCachedRemoteLibraryHint(hasRemoteAlbums: boolean): void {
  if (isTauriRuntimeAvailable()) return;
  if (_cachedRemoteLibraryHint === hasRemoteAlbums) return;
  _cachedRemoteLibraryHint = hasRemoteAlbums;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('maestro:remote-home-library-changed'));
  }
}

export function setRemoteHomeLibraryAvailable(available: boolean): void {
  if (isTauriRuntimeAvailable()) return;
  if (_remoteHomeLibraryAvailable === available) return;
  _remoteHomeLibraryAvailable = available;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('maestro:remote-home-library-changed'));
  }
}

/** Call when /api/library/remote/catalog returns albums for this account. */
export function markRemoteHomeLibraryFromCatalog(albumCount: number): void {
  if (isTauriRuntimeAvailable()) return;
  if (albumCount > 0) setRemoteHomeLibraryAvailable(true);
}

export function hasRemoteHomeLibraryAvailable(): boolean {
  return _remoteHomeLibraryAvailable;
}

/** v11.0.0: UI + local IPC routing on Connector — no probe gate. */
export function shouldUseLocalLibraryIpc(): boolean {
  return isTauriRuntimeAvailable();
}

/** Raw file-server probe — no backoff; prefer probeHomeLibraryFileServerWithBackoff. */
export async function probeHomeLibraryFileServer(): Promise<FileServerProbeResult> {
  if (!isTauriRuntimeAvailable()) {
    return { ok: false, reason: 'no-tauri', skipped: true, skipReason: 'no-tauri' };
  }
  try {
    if (!isConnectorAvailable()) {
      return { ok: false, reason: 'no-tauri', skipped: true, skipReason: 'no-tauri' };
    }
    const healthy = await connectorInvoke<boolean>('is_local_file_server_healthy');
    if (healthy) {
      console.log('[localLibraryIpcGate] Home library file-server IPC probe: healthy');
      return { ok: true };
    }
    console.warn('[localLibraryIpcGate] Home library file-server IPC probe: connector-reports-down');
    return { ok: false, reason: 'connector-reports-down' };
  } catch (e) {
    const message = String((e as Error)?.message || e);
    console.warn('[localLibraryIpcGate] Home library file-server IPC probe failed:', message);
    return { ok: false, error: message };
  }
}

/**
 * File-server probe with exponential backoff, source-offline gate, and single in-flight guard.
 * Never probes faster than 5s after a failure; doubles intervals when postMessage IPC is active.
 */
export async function probeHomeLibraryFileServerWithBackoff(
  opts?: { force?: boolean },
): Promise<FileServerProbeResult> {
  initHomeLibraryFileServerProbeGate();

  if (!isTauriRuntimeAvailable()) {
    return { ok: false, reason: 'no-tauri', skipped: true, skipReason: 'no-tauri' };
  }

  if (areAllLibrarySourcesOffline()) {
    return {
      ok: false,
      reason: 'sources-offline',
      skipped: true,
      skipReason: 'sources-offline',
    };
  }

  if (_fileServerProbeInFlight && _fileServerProbePromise) {
    return _fileServerProbePromise;
  }

  const now = Date.now();
  const minWait = effectiveProbeBackoffMs(_fileServerProbeFailures);
  if (!opts?.force && _fileServerProbeFailures > 0 && now - _fileServerProbeLastAttemptMs < minWait) {
    return { ..._fileServerProbeLastResult, skipped: true, skipReason: 'backoff' };
  }

  _fileServerProbeInFlight = true;
  _fileServerProbeLastAttemptMs = now;
  _fileServerProbePromise = (async () => {
    try {
      const result = await probeHomeLibraryFileServer();
      _fileServerProbeLastResult = result;
      if (result.ok) {
        resetFileServerProbeBackoff();
      } else if (!result.skipped) {
        _fileServerProbeFailures += 1;
      }
      return result;
    } finally {
      _fileServerProbeInFlight = false;
      _fileServerProbePromise = null;
    }
  })();

  return _fileServerProbePromise;
}

export function isLocalLibraryIpcUnavailable(): boolean {
  return isTauriRuntimeAvailable() && _localLibraryIpcAvailable === false;
}

/** Probe IPC and album count — data availability + migration banner; does not control UI visibility. */
export async function probeLocalLibraryHost(): Promise<boolean> {
  if (!isTauriRuntimeAvailable()) {
    _localLibraryHasAlbums = false;
    _hostProbeComplete = true;
    return false;
  }
  if (_coreLocalLibraryIpcConfirmed) return true;
  if (_hostProbeComplete && _localLibraryIpcAvailable === false) return false;
  if (!_hostProbePromise) {
    _hostProbePromise = (async () => {
      const ipcOk = await probeLocalLibraryIpc();
      if (!ipcOk) {
        _localLibraryHasAlbums = false;
        return false;
      }
      try {
        const stats = await connectorInvoke('local_library_get_stats');
        const n = Number((stats as { albums?: number; total_albums?: number })?.albums
          ?? (stats as { total_albums?: number })?.total_albums
          ?? 0);
        _localLibraryHasAlbums = n > 0;
        _coreLocalLibraryIpcConfirmed = true;
        _localLibraryIpcAvailable = true;
        if (n > 0) {
          markLocalCatalogBrowseReady(n);
        }
        if (n === 0) {
          console.log('[libraryService] Home library host — local IPC OK, catalog empty (Add Music available)');
        } else {
          console.log(`[libraryService] Home library host — ${n} albums in local catalog`);
        }
        return true;
      } catch {
        _localLibraryHasAlbums = false;
        return false;
      } finally {
        _hostProbeComplete = true;
        _hostProbePromise = null;
      }
    })();
  }
  return _hostProbePromise;
}

export async function probeLocalLibraryIpc(): Promise<boolean> {
  if (!isTauriRuntimeAvailable()) return false;
  if (_localLibraryIpcAvailable === false) return false;
  if (_localLibraryIpcAvailable === true) return true;
  if (!_probePromise) {
    _probePromise = (async () => {
      try {
        if (!isConnectorAvailable()) {
          _localLibraryIpcAvailable = false;
          return false;
        }
        await connectorInvoke('local_library_get_stats');
        _localLibraryIpcAvailable = true;
        _coreLocalLibraryIpcConfirmed = true;
        return true;
      } catch (e) {
        if (isLocalLibraryCommandError(e)) {
          markLocalLibraryIpcUnavailable(String((e as Error)?.message || e));
        } else {
          _localLibraryIpcAvailable = false;
        }
        return false;
      } finally {
        _probePromise = null;
      }
    })();
  }
  return _probePromise;
}

export function routeLocalLibrary<T>(
  localFn: () => Promise<T>,
  cloudFn: () => Promise<T>,
  opts?: { poisonOnAclFailure?: boolean },
): Promise<T> {
  if (isRemoteConnectorShell() || isRemoteLibraryViewer()) return cloudFn();
  if (!shouldUseLocalLibraryIpc()) return cloudFn();
  const poison = opts?.poisonOnAclFailure !== false;
  return localFn().catch((err) => {
    if (isLocalLibraryCommandError(err)) {
      if (poison) {
        markLocalLibraryIpcUnavailable(String((err as Error)?.message || err));
        _coreLocalLibraryIpcConfirmed = false;
      }
      // v11.0.0: home Connector must not fall back to cloud catalog on ACL/local IPC failure.
      if (isTauriRuntimeAvailable()) throw err;
      return cloudFn();
    }
    throw err;
  });
}
